import { ActionConfirmation } from "../ui/ActionConfirmation.js";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { ConfirmDialog } from "../ui/Dialog.js";
import { useSearchParams } from "react-router-dom";
import { timeRequestClosesAt, timeRequestOfferClosesAt } from "@wmf/shared/waitlist";
import { booking, type BookingOffering } from "../booking/api.js";
import { ApiError } from "../api.js";
import { formatDate, formatTime } from "../format.js";
import { useAsync } from "../useAsync.js";
import { FamilyOfferEmailPreferences } from "./FamilyOfferEmailPreferences.js";
import {
  waitlistApi,
  type WaitlistNotification,
  type FamilyRetentionFollowUpPreferencesInput,
  type WaitlistPattern,
  type WaitlistRequest,
} from "./api.js";

// Shared with the server, which writes the same description into the messages
// a request now generates. Imported *and* re-exported: `export ... from` alone
// forwards the name without binding it here, so the component below that calls
// it would fail at runtime while typechecking against the re-export.
import { describeWaitlistPattern } from "@wmf/shared/waitlist";

export { describeWaitlistPattern };

export type OfferActionOutcome =
  | { kind: "accepted"; message: string; operation?: "book" | "move" }
  | { kind: "rejected"; message: string }
  | { kind: "expired"; message: string }
  | { kind: "short_notice"; message: string };

export function expiredOfferOutcome(): OfferActionOutcome {
  return {
    kind: "expired",
    message:
      "That lesson offer expired while you were looking at it, so it has moved on to the next family.",
  };
}

export function offerFailureOutcome(cause: unknown): OfferActionOutcome | null {
  if (!(cause instanceof ApiError) || cause.status !== 409) return null;
  if (cause.code === "offer_expired") return expiredOfferOutcome();
  if (cause.code === "short_notice_tutor_approval_required") {
    return {
      kind: "short_notice",
      message: [
        "This lesson is now inside 24 hours and this offer can no longer be booked.",
        "Contact your tutor to ask whether they can approve this exact time.",
      ].join(" "),
    };
  }
  return null;
}

export function OfferActionNotice({
  outcome,
}: {
  outcome: OfferActionOutcome;
}) {
  return (
    <ActionConfirmation
      variant={outcome.kind === "accepted" ? "success" : "attention"}
      title={
        outcome.kind === "accepted"
          ? outcome.operation === "move"
            ? "Lesson moved"
            : "Lesson booked"
          : outcome.kind === "expired"
            ? "Lesson offer expired"
            : outcome.kind === "short_notice"
              ? "Tutor approval needed"
              : "Lesson offer declined"
      }
      description={outcome.message}
      reveal
    />
  );
}

/**
 * Offers shown on the family Home view.
 *
 * The read receipt is sent from an effect, after React has committed the row to
 * the page. Fetching a notification is not treated as seeing it: a failed or
 * abandoned render must not silently dismiss the only in-app alert. Being read
 * does not remove the offer either — it stays here until the family answers it.
 */
export function FamilyWaitlistOffers({
  studentId,
  onAccepted,
}: {
  studentId: string;
  onAccepted: () => void;
}) {
  const [searchParams] = useSearchParams();
  const legacyOfferContextUnavailable = searchParams.get("offer_context") === "unavailable";
  const rawOfferId = searchParams.get("offer");
  const selectedOfferId = rawOfferId && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(rawOfferId)
    ? rawOfferId.toLowerCase()
    : null;
  const loaded = useAsync(() => waitlistApi.notifications(), [studentId]);
  const context = useAsync(() => selectedOfferId ? waitlistApi.offerContext(selectedOfferId, studentId) : Promise.resolve(null), [studentId, selectedOfferId]);
  const currentContext = context.data?.id.toLowerCase() === selectedOfferId && context.data?.studentId === studentId ? context.data : null;
  const [busyId, setBusyId] = useState<string | null>(null);
  const [hidden, setHidden] = useState<ReadonlySet<string>>(new Set());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [outcome, setOutcome] = useState<OfferActionOutcome | null>(null);
  const [rejectMove, setRejectMove] = useState<WaitlistNotification | null>(
    null,
  );
  const markedRead = useRef(new Set<string>());
  const offerRefs = useRef(new Map<string, HTMLDivElement>());

  const offers = (loaded.data?.notifications ?? []).filter(
    (offer) => offer.studentId === studentId && !hidden.has(offer.id),
  );
  const offerIds = offers.map((offer) => offer.id).join(",");
  const selectedOfferVisible = offers.some((offer) => offer.id.toLowerCase() === selectedOfferId);
  const selectedOfferUnavailable = Boolean(
    selectedOfferId && loaded.data && !selectedOfferVisible && outcome === null,
  );

  useEffect(() => {
    if (!selectedOfferId) return;
    const target = offerRefs.current.get(selectedOfferId);
    if (!target) return;
    target.scrollIntoView({ block: "center", behavior: "smooth" });
    target.focus({ preventScroll: true });
  }, [offerIds, selectedOfferId]);

  useEffect(() => {
    for (const offer of offers) {
      if (!offer.unread || markedRead.current.has(offer.id)) continue;
      markedRead.current.add(offer.id);
      // A failed read receipt is safe to retry on the next page load; it does
      // not stop the family accepting or rejecting the offer in front of them.
      void waitlistApi.markOfferRead(offer.id).catch(() => {
        markedRead.current.delete(offer.id);
      });
    }
    // `offerIds` changes only when a different notification is actually shown.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerIds]);

  const dismiss = (offerId: string) => {
    setHidden((current) => new Set([...current, offerId]));
    setErrors((current) => {
      const next = { ...current };
      delete next[offerId];
      return next;
    });
  };

  async function act(offer: WaitlistNotification, action: "accept" | "reject") {
    setBusyId(offer.id);
    setOutcome(null);
    setErrors((current) => ({ ...current, [offer.id]: "" }));
    try {
      if (action === "accept") {
        await waitlistApi.acceptOffer(offer.id);
        dismiss(offer.id);
        setOutcome({
          kind: "accepted",
          operation: offer.purpose === "move" ? "move" : "book",
          message: `${offer.purpose === "move" ? "Moved to" : "Booked"} ${offer.serviceName} for ${formatDate(offer.startsAt)} at ${formatTime(offer.startsAt)}.`,
        });
        onAccepted();
      } else {
        await waitlistApi.rejectOffer(offer.id);
        dismiss(offer.id);
        setOutcome({
          kind: "rejected",
          message:
            offer.purpose === "move"
              ? "You declined this move. Your current lesson stays booked and any scheduled cancellation has been removed."
              : "You declined that lesson offer. It has been passed to the next family waiting for it.",
        });
      }
    } catch (cause) {
      const actionOutcome = offerFailureOutcome(cause);
      if (actionOutcome) {
        dismiss(offer.id);
        setOutcome(actionOutcome);
      } else {
        setErrors((current) => ({
          ...current,
          [offer.id]:
            cause instanceof ApiError
              ? cause.message
              : "That could not be recorded.",
        }));
      }
    } finally {
      setBusyId(null);
    }
  }

  if (loaded.loading && !loaded.data) return null;
  if (loaded.error) {
    return (
      <div className="error" role="alert">
        We could not check for lesson offers right now.
        <button type="button" className="quiet small" onClick={loaded.reload}>Retry offers</button>
      </div>
    );
  }
  if (offers.length === 0 && outcome === null && !selectedOfferUnavailable && !legacyOfferContextUnavailable) return null;

  return (
    <section aria-labelledby="waitlist-offers-heading">
      {(offers.length > 0 || outcome !== null || legacyOfferContextUnavailable || selectedOfferUnavailable) && (
        <h2 id="waitlist-offers-heading">Lesson offer</h2>
      )}
      {rejectMove && (
        <ConfirmDialog
          title="Decline this move?"
          description={`Your lesson on ${formatDate(rejectMove.sourceStartsAt!)} at ${formatTime(rejectMove.sourceStartsAt!)} stays booked. Any scheduled cancellation will be removed.`}
          confirmLabel="Keep current lesson"
          busy={busyId === rejectMove.id}
          onClose={() => setRejectMove(null)}
          onConfirm={() => {
            void act(rejectMove, "reject");
            setRejectMove(null);
          }}
        />
      )}
      {outcome && <OfferActionNotice outcome={outcome} />}
      {selectedOfferUnavailable && (
        <div className="notice" role="status">
          {context.loading ? "Checking this lesson offer…" : currentContext ? <>
            {currentContext.serviceName} with {currentContext.tutorName} · {formatDate(currentContext.startsAt)} at {formatTime(currentContext.startsAt)}.
            {" "}{currentContext.status === "expired" ? "This offer has expired." : currentContext.status === "accepted" ? "This offer has already been accepted. Check Lessons for its current status." : currentContext.status === "rejected" ? "This offer was declined." : currentContext.status === "superseded" ? "This offer was replaced by a newer request." : "This offer is no longer available to act on here."}
          </> : "This offer is unavailable for the selected learner. It may have been removed or the link may be incomplete."}
          {context.error && <button type="button" className="quiet small" onClick={context.reload}>Check offer again</button>}
        </div>
      )}
      {legacyOfferContextUnavailable && !selectedOfferId && (
        <div className="notice" role="status">
          This notification did not include a verifiable offer link. Check the current lesson offers below before taking action.
        </div>
      )}
      {offers.length > 0 && (
        <div className="panel">
          {offers.map((offer) => (
            <div
              className="slot"
              key={offer.id}
              tabIndex={offer.id.toLowerCase() === selectedOfferId ? -1 : undefined}
              ref={(node) => {
                if (node) offerRefs.current.set(offer.id.toLowerCase(), node);
                else offerRefs.current.delete(offer.id.toLowerCase());
              }}
            >
              <time>{formatTime(offer.startsAt)}</time>
              <div style={{ flex: 1 }}>
                <div>{formatDate(offer.startsAt)}</div>
                <div className="small muted">
                  {offer.studentName} · {offer.serviceName} with{" "}
                  {offer.tutorName} · ends {formatTime(offer.endsAt)}
                </div>
                {offer.purpose === "move" && offer.sourceStartsAt && (
                  <div className="small">
                    Move from {formatDate(offer.sourceStartsAt)} at{" "}
                    {formatTime(offer.sourceStartsAt)}.{" "}
                    {offer.fallback === "cancel"
                      ? offer.cancellationAt
                        ? `If you have not accepted a move, the current lesson will be cancelled on ${formatDate(offer.cancellationAt)} at ${formatTime(offer.cancellationAt)} as you asked.`
                        : "If you have not accepted a move, the current lesson will be cancelled as you asked."
                      : "Your current lesson stays booked unless you accept."}
                  </div>
                )}
                {offer.unread && <span className="tag">New</span>}
                {offer.expiresAt && (
                  <div className="small muted">
                    Please answer by {formatDate(offer.expiresAt)} at{" "}
                    {formatTime(offer.expiresAt)}.
                  </div>
                )}
                {errors[offer.id] && (
                  <div className="error small">{errors[offer.id]}</div>
                )}
              </div>
              <div className="row">
                <button
                  className="quiet small"
                  disabled={busyId === offer.id}
                  onClick={() =>
                    offer.purpose === "move"
                      ? setRejectMove(offer)
                      : void act(offer, "reject")
                  }
                  type="button"
                >
                  {offer.purpose === "move" ? "Keep current lesson" : "Reject"}
                </button>
                <button
                  className="small"
                  disabled={busyId === offer.id}
                  onClick={() => void act(offer, "accept")}
                  type="button"
                >
                  {busyId === offer.id
                    ? "…"
                    : offer.purpose === "move"
                      ? "Accept and move"
                      : "Accept and book"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export function FamilyWaitlistRequests({
  studentId,
  refreshNonce,
}: {
  studentId: string;
  refreshNonce: number;
}) {
  const loaded = useAsync(
    () => waitlistApi.requests(),
    [studentId, refreshNonce],
  );
  const offeringQuery = useAsync(() => booking.offerings(studentId), [studentId]);
  const currentOfferings = offeringQuery.data?.portal_student_id === studentId
    ? offeringQuery.data.offerings.filter((offering) =>
        Boolean(offering.tutor_id && offering.tutor_name && offering.service_id > 0))
    : null;
  const requests = (loaded.data?.requests ?? []).filter(
    (request) =>
      request.studentId === studentId &&
      (request.status === "pending" ||
        request.status === "approved" ||
        (request.kind === "pattern" && request.status === "rejected") ||
        request.kind === "specific_time"),
  );

  return (
    <section aria-labelledby="waitlist-requests-heading">
      <h2 id="waitlist-requests-heading">Your waiting list requests</h2>
      <p className="small muted">
        Recurring preferences stay active until you pause or withdraw them. Offers still depend on an actual released time and your tutor’s approval.
      </p>
      <div className="panel">
        {loaded.loading && <p className="empty">Loading…</p>}
        {loaded.error && <div className="error">{loaded.error}</div>}
        {!loaded.loading && !loaded.error && requests.length === 0 && (
          <p className="empty">No open waiting list requests.</p>
        )}
        {requests.map((request) => (
          <FamilyRequestRow
            key={request.id}
            request={request}
            offerings={currentOfferings}
            onChanged={loaded.reload}
          />
        ))}
      </div>
      <FamilyOfferEmailPreferences studentId={studentId} />
      <FamilyRetentionFollowUpPreferences studentId={studentId} />
      <p className="small">
        Need another option? <a href={`/book?student=${encodeURIComponent(studentId)}`}>Check current bookable times or request a specific date and time</a>.
      </p>
      {offeringQuery.error && (
        <p className="small muted" role="status">
          Current tutor and lesson choices could not be checked. Retry them before editing a preference.
          <button type="button" className="quiet small" onClick={offeringQuery.reload}>Retry choices</button>
        </p>
      )}
    </section>
  );
}

type WindowDraft = { weekday: number; start: string; end: string };
type PreferenceDraft = {
  offeringKey: string;
  windows: WindowDraft[];
  shortNoticeOptIn: boolean;
};

const WEEKDAY_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
function clockText(minutes: number): string {
  if (minutes === 1440) return "24:00";
  return `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;
}
function parseClock(value: string, allowMidnightEnd: boolean): number | null {
  const match = /^(\\d{1,2}):(\\d{2})$/.exec(value.trim());
  if (!match) return null;
  const hours = Number(match[1]), minutes = Number(match[2]);
  if (minutes > 59 || hours > 24 || (hours === 24 && (!allowMidnightEnd || minutes !== 0))) return null;
  return hours * 60 + minutes;
}
function nextEditableWindow(windows: readonly WindowDraft[]): WindowDraft | null {
  const choices = [[9, 10], [10, 11], [13, 14], [14, 15], [16, 17], [17, 18]] as const;
  for (let weekday = 0; weekday < 7; weekday += 1) {
    for (const [startHour, endHour] of choices) {
      const startsMinutes = startHour * 60;
      const endsMinutes = endHour * 60;
      const overlaps = windows.some((window) => {
        if (window.weekday !== weekday) return false;
        const starts = parseClock(window.start, false);
        const ends = parseClock(window.end, true);
        return starts !== null && ends !== null && startsMinutes < ends && endsMinutes > starts;
      });
      if (!overlaps) {
        return {
          weekday,
          start: `${String(startHour).padStart(2, "0")}:00`,
          end: `${String(endHour).padStart(2, "0")}:00`,
        };
      }
    }
  }
  return null;
}

function offeringKey(offering: BookingOffering): string {
  return `${offering.tutor_id}:${offering.service_id}`;
}

function FamilyRequestRow({
  request,
  offerings,
  onChanged,
}: {
  request: WaitlistRequest;
  offerings: BookingOffering[] | null;
  onChanged: () => void;
}) {
  const [confirm, setConfirm] = useState(false);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<PreferenceDraft | null>(null);

  function beginEdit() {
    const existing = offerings?.find(
      (offering) =>
        offering.tutor_id === request.tutorId &&
        offering.service_id === request.serviceId,
    );
    setDraft({
      offeringKey: existing ? offeringKey(existing) : "",
      windows: request.patterns.map((pattern) => ({
        weekday: pattern.weekday,
        start: clockText(pattern.startsMinutes),
        end: clockText(pattern.endsMinutes),
      })),
      shortNoticeOptIn: request.shortNoticeOptIn ?? false,
    });
    setError(null);
    setEditing(true);
  }

  async function runChange(action: "withdraw" | "pause" | "resume") {
    setBusy(true);
    setError(null);
    try {
      if (action === "withdraw") await waitlistApi.withdrawRequest(request.id);
      else if (action === "pause") await waitlistApi.pauseRequest(request.id);
      else await waitlistApi.resumeRequest(request.id);
      setConfirm(false);
      onChanged();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not update this request.");
    } finally {
      setBusy(false);
    }
  }

  async function saveEdit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!draft) return;
    const selected = offerings?.find((offering) => offeringKey(offering) === draft.offeringKey);
    if (!selected?.tutor_id) {
      setError("Choose a current tutor and lesson option before saving.");
      return;
    }
    const patterns: WaitlistPattern[] = [];
    for (const window of draft.windows) {
      const startsMinutes = parseClock(window.start, false);
      const endsMinutes = parseClock(window.end, true);
      if (startsMinutes === null || endsMinutes === null || endsMinutes <= startsMinutes) {
        setError("Enter a valid start and end time for every window.");
        return;
      }
      patterns.push({ weekday: window.weekday, startsMinutes, endsMinutes });
    }
    setBusy(true);
    setError(null);
    try {
      await waitlistApi.updateRequest(request.id, {
        tutorId: selected.tutor_id,
        serviceId: selected.service_id,
        patterns,
        shortNoticeOptIn: draft.shortNoticeOptIn,
      });
      setEditing(false);
      setDraft(null);
      onChanged();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not save this preference.");
    } finally {
      setBusy(false);
    }
  }

  const deadlineRequest = {
    purpose: request.purpose ?? null,
    actBy: request.actBy ? new Date(request.actBy) : null,
    requestedStartsAt: request.requestedStartsAt
      ? new Date(request.requestedStartsAt)
      : null,
  };
  const deadline = timeRequestOfferClosesAt(deadlineRequest)?.toISOString() ?? null;
  const cancellationAt = timeRequestClosesAt(deadlineRequest)?.toISOString() ?? null;
  const open = request.status === "pending" || request.status === "approved";
  const canEditOrWithdraw = open || request.status === "rejected";
  const selectionIsCurrent = Boolean(draft?.offeringKey && offerings?.some((offering) => offeringKey(offering) === draft.offeringKey));

  return (
    <div className="slot">
      <div style={{ flex: 1 }}>
        <div>{request.serviceName} with {request.tutorName}</div>
        <div className="small muted">
          {request.kind === "specific_time" && request.requestedStartsAt
            ? `${formatDate(request.requestedStartsAt)} at ${formatTime(request.requestedStartsAt)}`
            : request.patterns.map(describeWaitlistPattern).join(" · ")}
          {request.kind === "pattern" && (
            <div>
              {request.pausedAt
                ? request.status === "approved"
                  ? "Paused. Resume to keep this tutor-approved preference active."
                  : "Paused. Resume to continue waiting for tutor approval."
                : request.status === "approved"
                  ? "Active recurring preference."
                  : request.status === "rejected"
                    ? "Declined. Edit to ask the tutor to review this preference again."
                    : "Waiting for tutor approval."}
              {request.shortNoticeOptIn &&
                " You’re interested in short-notice lessons. " +
                  "The assigned tutor must approve each exact time before it is offered."}
              {request.status === "rejected" && request.decisionReason && (
                <div>Tutor’s reason: {request.decisionReason}</div>
              )}
            </div>
          )}
          {request.purpose === "move" && request.sourceStartsAt && (
            <div>
              Instead of {formatDate(request.sourceStartsAt)} at {formatTime(request.sourceStartsAt)} ·{" "}
              {request.fallback === "cancel" ? "Cancel current lesson" : "Keep current lesson"}
              {deadline && ` · answer by ${formatDate(deadline)} at ${formatTime(deadline)}`}
              {request.fallback === "cancel" && cancellationAt &&
                (request.status === "pending" || request.status === "approved") && (
                  <div>
                    Scheduled cancellation: {formatDate(cancellationAt)} at {formatTime(cancellationAt)} if the move has not been accepted.
                  </div>
                )}
            </div>
          )}
          {error && <div className="error" role="alert">{error}</div>}
        </div>
      </div>
      <span className="tag">
        {request.pausedAt
          ? "Paused"
          : request.status === "pending"
            ? request.kind === "specific_time"
              ? "Subject to tutor approval"
              : "Waiting for tutor"
            : request.status === "approved"
              ? "Waiting for a time"
              : request.status === "rejected"
                ? "Declined"
                : request.status === "lapsed"
                ? "Expired"
                : request.status === "superseded"
                  ? "Lesson changed"
                  : request.status}
      </span>
      {request.kind === "pattern" && canEditOrWithdraw && !editing && (
        <>
          {open && (
            request.pausedAt ? (
              <button className="quiet small" type="button" disabled={busy} onClick={() => void runChange("resume")}>
                Resume
              </button>
            ) : (
              <button className="quiet small" type="button" disabled={busy} onClick={() => void runChange("pause")}>
                Pause
              </button>
            )
          )}
          <button className="quiet small" type="button" disabled={busy} onClick={beginEdit}>Edit</button>
          <button className="quiet small" type="button" disabled={busy} onClick={() => setConfirm(true)}>Withdraw</button>
        </>
      )}
      {request.kind === "specific_time" && open && (
        <button
          className="quiet small"
          type="button"
          disabled={busy}
          onClick={() => request.fallback === "cancel" ? setConfirm(true) : void runChange("withdraw")}
        >
          Withdraw
        </button>
      )}
      {editing && draft && (
        <form className="waitlist-preference-editor" onSubmit={(event) => void saveEdit(event)}>
          <label>
            Tutor and lesson
            <select
              value={draft.offeringKey}
              disabled={busy || offerings === null}
              onChange={(event) => setDraft({ ...draft, offeringKey: event.target.value })}
            >
              <option value="">Choose a current option</option>
              {(offerings ?? []).map((offering) => (
                <option key={offeringKey(offering)} value={offeringKey(offering)}>
                  {offering.tutor_name} — {offering.service_name ?? `Lesson ${offering.service_id}`}
                </option>
              ))}
            </select>
          </label>
          {offerings === null && <p className="small muted">Checking current lesson options…</p>}
          <fieldset>
            <legend>Preferred days and times</legend>
            {draft.windows.map((window, index) => (
              <div className="row" key={index}>
                <label>
                  Day
                  <select
                    value={window.weekday}
                    disabled={busy}
                    onChange={(event) => setDraft({
                      ...draft,
                      windows: draft.windows.map((item, i) => i === index ? { ...item, weekday: Number(event.target.value) } : item),
                    })}
                  >
                    {WEEKDAY_NAMES.map((day, weekday) => <option key={day} value={weekday}>{day}</option>)}
                  </select>
                </label>
                <label>
                  From
                  <input
                    type="text"
                    inputMode="text"
                    aria-label={`Window ${index + 1} start time`}
                    value={window.start}
                    disabled={busy}
                    onChange={(event) => setDraft({
                      ...draft,
                      windows: draft.windows.map((item, i) => i === index ? { ...item, start: event.target.value } : item),
                    })}
                  />
                </label>
                <label>
                  Until
                  <input
                    type="text"
                    inputMode="numeric"
                    aria-label={`Window ${index + 1} end time`}
                    value={window.end}
                    disabled={busy}
                    onChange={(event) => setDraft({
                      ...draft,
                      windows: draft.windows.map((item, i) => i === index ? { ...item, end: event.target.value } : item),
                    })}
                  />
                </label>
                <button
                  className="quiet small"
                  type="button"
                  disabled={busy || draft.windows.length <= 1}
                  aria-label={`Remove window ${index + 1}`}
                  onClick={() => setDraft({ ...draft, windows: draft.windows.filter((_, i) => i !== index) })}
                >
                  Remove
                </button>
              </div>
            ))}
            <button
              className="quiet small"
              type="button"
              disabled={busy || draft.windows.length >= 21 || nextEditableWindow(draft.windows) === null}
              onClick={() => {
                const suggested = nextEditableWindow(draft.windows);
                if (suggested) setDraft({ ...draft, windows: [...draft.windows, suggested] });
              }}
            >
              Add another time
            </button>
          </fieldset>
          <label>
            <input
              type="checkbox"
              checked={draft.shortNoticeOptIn}
              disabled={busy}
              onChange={(event) => setDraft({ ...draft, shortNoticeOptIn: event.target.checked })}
            />
            I’m interested in lessons with less than 24 hours’ notice
          </label>
          <p className="small muted">
            The assigned tutor must approve each short-notice lesson for this learner
            before an offer is sent. This preference does not book or approve a lesson.
          </p>
          <p className="small muted">
            Widening your times or changing tutor or lesson needs fresh tutor approval.
            Narrowing your times, or pausing and resuming, keeps the current approval.
          </p>
          <div className="row">
            <button type="submit" disabled={busy || !selectionIsCurrent}>
              {busy ? "Saving…" : "Save preference"}
            </button>
            <button className="quiet small" type="button" disabled={busy} onClick={() => { setEditing(false); setDraft(null); }}>
              Cancel
            </button>
          </div>
        </form>
      )}
      {confirm && (
        <ConfirmDialog
          title={request.kind === "specific_time" && request.purpose === "move" ? "Withdraw this move request?" : "Withdraw this preference?"}
          description={request.kind === "specific_time" && request.purpose === "move" && request.sourceStartsAt
            ? `Your lesson on ${formatDate(request.sourceStartsAt)} at ${formatTime(request.sourceStartsAt)} will stay booked.`
            : "This recurring preference will be withdrawn from the waiting list."}
          confirmLabel="Withdraw request"
          busy={busy}
          onConfirm={() => void runChange("withdraw")}
          onClose={() => setConfirm(false)}
        />
      )}
    </div>
  );
}

function FamilyRetentionFollowUpPreferences({ studentId }: { studentId: string }) {
  const loaded = useAsync(
    () => waitlistApi.retentionFollowUpPreferences(studentId),
    [studentId],
  );
  const [enabled, setEnabled] = useState(false);
  const [quietHoursStart, setQuietHoursStart] = useState("");
  const [quietHoursEnd, setQuietHoursEnd] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!loaded.data?.enabled) return;
    setEnabled(loaded.data.retentionFollowUpsEnabled);
    setQuietHoursStart(loaded.data.quietHoursStart ?? "");
    setQuietHoursEnd(loaded.data.quietHoursEnd ?? "");
    setError(null);
    setSaved(false);
  }, [loaded.data]);

  if (!loaded.loading && !loaded.error && loaded.data?.enabled === false) return null;

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setSaved(false);
    if ((quietHoursStart === "") !== (quietHoursEnd === "")) {
      setError("Choose both quiet-hours times or leave both blank.");
      return;
    }
    if (quietHoursStart !== "" && quietHoursStart === quietHoursEnd) {
      setError("Quiet hours need a start and a different end time.");
      return;
    }
    const input: FamilyRetentionFollowUpPreferencesInput = {
      retentionFollowUpsEnabled: enabled,
      quietHoursStart: quietHoursStart || null,
      quietHoursEnd: quietHoursEnd || null,
    };
    setSaving(true);
    try {
      await waitlistApi.saveRetentionFollowUpPreferences(studentId, input);
      setSaved(true);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not save follow-up preferences.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="panel">
      <h3>Optional lesson follow-up emails</h3>
      {loaded.loading && <p className="empty">Loading preferences…</p>}
      {loaded.error && <div className="error" role="alert">{String(loaded.error)}</div>}
      {loaded.data?.enabled && (
        <form onSubmit={(event) => void save(event)}>
          <p className="small muted">
            A staff member may queue one optional check-in email for this learner,
            no more than once every 30 days. Messages go only to this confirmed
            guardian account; we never email the learner.
          </p>
          <label>
            <input
              checked={enabled}
              disabled={saving || loaded.loading}
              onChange={(event) => setEnabled(event.target.checked)}
              type="checkbox"
            />{" "}
            Email me occasional lesson follow-ups for this learner
          </label>
          <div className="form-row">
            <label>
              Quiet hours start
              <input
                aria-label="Quiet hours start"
                disabled={saving}
                onChange={(event) => setQuietHoursStart(event.target.value)}
                type="time"
                value={quietHoursStart}
              />
            </label>
            <label>
              Quiet hours end
              <input
                aria-label="Quiet hours end"
                disabled={saving}
                onChange={(event) => setQuietHoursEnd(event.target.value)}
                type="time"
                value={quietHoursEnd}
              />
            </label>
          </div>
          <p className="small muted">
            Times use Europe/London. Leave both blank for no quiet hours.
          </p>
          {error && <div className="error" role="alert">{error}</div>}
          {saved && <p className="small" role="status">Preferences saved.</p>}
          <button className="quiet small" disabled={saving} type="submit">
            {saving ? "Saving…" : "Save preferences"}
          </button>
        </form>
      )}
    </div>
  );
}
